/**
 * Content Packs Management Page
 *
 * Admin interface for managing content packs (lucky charm themes, backgrounds, etc.)
 */

import { useState, useCallback, useEffect, useRef } from 'react'
import { Package, Plus, Upload, Trash2, Edit, Image, FolderInput, X, Save, ChevronDown, ChevronUp, Settings2, Film } from 'lucide-react'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Textarea } from '@/components/ui/Textarea'
import { Select } from '@/components/ui/Select'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/Tabs'
import { Modal } from '@/components/common/Modal'
import {
  useContentPackList,
  useContentPack,
  useCreateContentPack,
  useUpdateContentPack,
  useDeleteContentPack,
  useUploadItems,
  useDeleteItem,
  useUpdateItemMetadata,
  useMigrateFilesystem,
} from '@/features/content-packs/hooks/useContentPacks'
import type { ContentPack, ContentPackItem, ContentPackCreate, ContentPackUpdate, ItemRenderMetadata, PackMetadata, OverlayConfig } from '@/lib/api/contentPacks'
import { TextAreaEditor } from '@/features/content-packs/components/TextAreaEditor'
import { FontLibrarySection } from '@/features/fonts/components/FontLibrarySection'
import { FontPicker } from '@/components/FontPicker'
import clsx from 'clsx'

// ============================================================================
// Status Badge
// ============================================================================

function StatusBadge({ status }: { status: string }) {
  const styles: Record<string, string> = {
    draft: 'bg-yellow-100 text-yellow-800',
    active: 'bg-green-100 text-green-800',
    archived: 'bg-gray-100 text-gray-600',
  }
  const labels: Record<string, string> = {
    draft: '草稿',
    active: '已激活',
    archived: '已归档',
  }
  return (
    <span className={clsx('inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium', styles[status] || styles.draft)}>
      {labels[status] || status}
    </span>
  )
}

function TypeBadge({ type }: { type: string }) {
  const labels: Record<string, string> = {
    lucky_charm_theme: '签文主题',
    energy_outfit_image: '能量穿衣图',
    mbti_official_character: 'MBTI 人格图',
    mbti_contrast_character: 'MBTI 反差人格图',
  }
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
      {labels[type] || type}
    </span>
  )
}

// ============================================================================
// Create Pack Dialog
// ============================================================================

function CreatePackDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const createMutation = useCreateContentPack()
  const [form, setForm] = useState<ContentPackCreate>({
    type: 'lucky_charm_theme',
    name: '',
    display_name: '',
    description: '',
    status: 'draft',
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.name || !form.display_name) return
    await createMutation.mutateAsync(form)
    setForm({ type: 'lucky_charm_theme', name: '', display_name: '', description: '', status: 'draft' })
    onClose()
  }

  return (
    <Modal isOpen={open} onClose={onClose} title="创建内容包" size="lg">
      <form onSubmit={handleSubmit} className="space-y-4">
        <Select
          label="类型"
          value={form.type}
          onChange={(e) => setForm(prev => ({ ...prev, type: e.target.value }))}
          options={[
            { value: 'lucky_charm_theme', label: '签文主题' },
            { value: 'energy_outfit_image', label: '能量穿衣图' },
            { value: 'mbti_official_character', label: 'MBTI 人格图' },
            { value: 'mbti_contrast_character', label: 'MBTI 反差人格图' },
          ]}
        />
        <Input
          label="标识名"
          placeholder="如 classic_ink"
          value={form.name}
          onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
          required
          helperText="唯一标识，创建后不可更改"
        />
        <Input
          label="显示名称"
          placeholder="如 经典水墨"
          value={form.display_name}
          onChange={(e) => setForm(prev => ({ ...prev, display_name: e.target.value }))}
          required
        />
        <Textarea
          label="描述"
          placeholder="内容包描述..."
          value={form.description || ''}
          onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value }))}
          rows={3}
        />
        <Select
          label="状态"
          value={form.status || 'draft'}
          onChange={(e) => setForm(prev => ({ ...prev, status: e.target.value }))}
          options={[
            { value: 'draft', label: '草稿' },
            { value: 'active', label: '激活' },
          ]}
        />
        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="outline" onClick={onClose}>取消</Button>
          <Button type="submit" loading={createMutation.isPending}>
            <Plus className="w-4 h-4 mr-1" />
            创建
          </Button>
        </div>
      </form>
    </Modal>
  )
}

// ============================================================================
// Metadata Helpers
// ============================================================================

function hasCustomMetadata(metadata: ItemRenderMetadata | undefined): boolean {
  if (!metadata) return false
  return !!(metadata.mbti_type || metadata.contrast_key || metadata.variant_key || metadata.tags?.length || metadata.asset_role ||
    metadata.view_port || metadata.text_box || metadata.font_color ||
    metadata.outline_color || metadata.rotation_angle !== undefined ||
    metadata.font_scale_x !== undefined || metadata.line_spacing !== undefined ||
    metadata.text_align !== undefined || metadata.text_effect !== undefined ||
    metadata.shadow_blur !== undefined || metadata.shadow_opacity !== undefined ||
    metadata.shadow_color !== undefined || metadata.motion || metadata.live_photo ||
    metadata.display_name || metadata.description)
}

function normalizeMetadataKey(value: string): string | undefined {
  const normalized = value.trim().toLowerCase().replace(/[\s-]+/g, '_')
  return normalized || undefined
}

const PLAYBACK_SPEED_OPTIONS = [1, 0.75, 0.5, 0.35, 0.25]

function getMotionObjectName(metadata: ItemRenderMetadata | undefined): string | undefined {
  const motion = metadata?.motion
  return motion?.object_name || motion?.video_object_name || metadata?.live_photo?.video_object_name
}

function getPlaybackSpeed(metadata: ItemRenderMetadata | undefined): number {
  const value = metadata?.motion?.playback_speed
  return typeof value === 'number' && Number.isFinite(value) ? value : 0.25
}

function formatPlaybackSpeed(speed: number): string {
  return `${Number(speed.toFixed(2))}x`
}

function MotionVideoPreview({
  videoUrl,
  posterUrl,
  playbackSpeed,
  className,
  controls = true,
}: {
  videoUrl: string
  posterUrl?: string
  playbackSpeed: number
  className?: string
  controls?: boolean
}) {
  const videoRef = useRef<HTMLVideoElement>(null)

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.playbackRate = playbackSpeed
    }
  }, [playbackSpeed, videoUrl])

  return (
    <video
      ref={videoRef}
      src={videoUrl}
      poster={posterUrl}
      className={className}
      controls={controls}
      muted
      loop
      playsInline
      preload="metadata"
      onClick={e => e.stopPropagation()}
    />
  )
}

// ============================================================================
// Item Metadata Dialog
// ============================================================================

function ItemMetadataDialog({
  open,
  onClose,
  item,
  packId,
  items,
}: {
  open: boolean
  onClose: () => void
  item: ContentPackItem
  packId: string
  items?: ContentPackItem[]
}) {
  const updateMutation = useUpdateItemMetadata()
  const [form, setForm] = useState<ItemRenderMetadata>(() => ({ ...item.metadata }))
  const [showFullscreen, setShowFullscreen] = useState(false)

  const isInheriting = !hasCustomMetadata(form)
  const motionObjectName = getMotionObjectName(form) || getMotionObjectName(item.metadata)
  const playbackSpeed = getPlaybackSpeed(form)
  const hasMotionVideo = Boolean(motionObjectName && item.video_url)

  const handleSave = async () => {
    await updateMutation.mutateAsync({ packId, itemId: item.id, metadata: form })
    onClose()
  }

  const handleReset = async () => {
    const empty: ItemRenderMetadata = {}
    await updateMutation.mutateAsync({ packId, itemId: item.id, metadata: empty })
    setForm({})
    onClose()
  }

  const updateField = <K extends keyof ItemRenderMetadata>(key: K, value: ItemRenderMetadata[K]) => {
    setForm(prev => ({ ...prev, [key]: value }))
  }

  const updatePlaybackSpeed = (speed: number) => {
    setForm(prev => {
      const motion = { ...(prev.motion || {}) }
      motion.playback_speed = speed
      if (!motion.object_name && !motion.video_object_name) {
        const videoObjectName = prev.live_photo?.video_object_name || item.metadata?.live_photo?.video_object_name
        if (videoObjectName) motion.video_object_name = videoObjectName
      }
      return { ...prev, motion }
    })
  }

  return (
    <Modal isOpen={open} onClose={onClose} title="项目元数据配置" size="2xl">
      <div className="space-y-5">
        {/* Media preview + header */}
        {hasMotionVideo && item.video_url ? (
          <div className="rounded-lg overflow-hidden bg-black border border-gray-200">
            <div
              className="relative cursor-pointer group"
              onClick={() => setShowFullscreen(true)}
            >
              <MotionVideoPreview
                videoUrl={item.video_url}
                posterUrl={item.thumbnail_url}
                playbackSpeed={playbackSpeed}
                className="w-full max-h-80 object-contain bg-black"
              />
              <div className="absolute top-2 left-2 bg-black/65 text-white rounded px-2 py-1 text-xs">
                当前速度 {formatPlaybackSpeed(playbackSpeed)}
              </div>
              <button
                type="button"
                className="absolute top-2 right-2 bg-black/65 text-white rounded px-2 py-1 text-xs hover:bg-black/80"
                onClick={(e) => {
                  e.stopPropagation()
                  setShowFullscreen(true)
                }}
              >
                全屏
              </button>
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center pointer-events-none">
                <span className="opacity-0 group-hover:opacity-100 transition-opacity text-sm text-white bg-black/50 px-3 py-1 rounded-full">
                  点击全屏查看视频
                </span>
              </div>
            </div>
          </div>
        ) : item.thumbnail_url && (
          <div
            className="rounded-lg overflow-hidden bg-gray-100 border border-gray-200 cursor-pointer group relative"
            onClick={() => setShowFullscreen(true)}
          >
            <img
              src={item.thumbnail_url}
              alt={item.filename}
              className="w-full max-h-80 object-contain"
            />
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
              <span className="opacity-0 group-hover:opacity-100 transition-opacity text-sm text-white bg-black/50 px-3 py-1 rounded-full">
                点击全屏查看
              </span>
            </div>
          </div>
        )}
        {/* Fullscreen lightbox */}
        {showFullscreen && (
          <div
            className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center cursor-pointer"
            onClick={() => setShowFullscreen(false)}
          >
            <button
              className="absolute top-4 right-4 text-white/70 hover:text-white text-3xl leading-none"
              onClick={() => setShowFullscreen(false)}
            >
              &times;
            </button>
            {hasMotionVideo && item.video_url ? (
              <MotionVideoPreview
                videoUrl={item.video_url}
                posterUrl={item.thumbnail_url}
                playbackSpeed={playbackSpeed}
                className="max-w-[90vw] max-h-[90vh] object-contain"
              />
            ) : item.thumbnail_url ? (
              <img
                src={item.thumbnail_url}
                alt={item.filename}
                className="max-w-[90vw] max-h-[90vh] object-contain"
                onClick={e => e.stopPropagation()}
              />
            ) : null}
          </div>
        )}
        <div className="flex items-center gap-3 pb-3 border-b border-gray-200">
          <div>
            <p className="text-sm font-medium text-gray-900">{item.filename}</p>
            <p className="text-xs text-gray-500">
              {isInheriting ? (
                <span className="text-amber-600">继承包默认 (点击字段覆盖)</span>
              ) : (
                <span className="text-green-600">自定义配置</span>
              )}
            </p>
          </div>
        </div>

        {/* Interactive rendering editor */}
        <TextAreaEditor
          packId={packId}
          metadata={form}
          onChange={setForm}
          backgroundItemId={item.id}
          items={items}
        />

        {motionObjectName && (
          <div className="space-y-3 pt-3 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium text-gray-700">动态视频</h4>
              <span className="text-xs text-gray-500 truncate max-w-[420px]" title={motionObjectName}>
                {motionObjectName}
              </span>
            </div>
            <div className="grid grid-cols-5 gap-2">
              {PLAYBACK_SPEED_OPTIONS.map(speed => (
                <button
                  key={speed}
                  type="button"
                  onClick={() => updatePlaybackSpeed(speed)}
                  className={clsx(
                    'rounded-md border px-3 py-2 text-sm font-medium transition-colors',
                    Math.abs(playbackSpeed - speed) < 0.001
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
                  )}
                >
                  {formatPlaybackSpeed(speed)}
                </button>
              ))}
            </div>
            <Input
              label="自定义播放速度 (motion.playback_speed)"
              type="number"
              min="0.2"
              max="1"
              step="0.05"
              value={playbackSpeed}
              onChange={e => {
                const parsed = Number(e.target.value)
                if (Number.isFinite(parsed)) updatePlaybackSpeed(Math.min(1, Math.max(0.2, parsed)))
              }}
              helperText="逐条素材配置。保存后今日运势卡片会使用该速度，不做全局统一。"
            />
          </div>
        )}

        {/* Display fields */}
        <div className="space-y-4 pt-3 border-t border-gray-200">
          <h4 className="text-sm font-medium text-gray-700">展示信息</h4>
          <Input
            label="MBTI 类型 (mbti_type)"
            placeholder="如 INTJ"
            value={form.mbti_type ?? ''}
            onChange={e => updateField('mbti_type', e.target.value.toUpperCase() || undefined)}
            helperText="MBTI 人格图和反差人格图都会按这个字段精确匹配。"
          />
          <Input
            label="反差人格标签 (contrast_key)"
            placeholder="如 certified_psycho"
            value={form.contrast_key ?? ''}
            onChange={e => updateField('contrast_key', normalizeMetadataKey(e.target.value))}
            helperText="反差人格图的精确匹配标签。模型工具调用会用这个字段选图，避免按文件名或模糊标签误配。"
          />
          <Input
            label="变体标签 (variant_key)"
            placeholder="如 certified_psycho_v1"
            value={form.variant_key ?? ''}
            onChange={e => updateField('variant_key', normalizeMetadataKey(e.target.value))}
            helperText="可选。用于同一反差人格下区分版本，旧工具也会兼容读取。"
          />
          <Input
            label="标签 (tags)"
            placeholder="逗号分隔，如: mbti, INTJ, analyst, architect"
            value={form.tags?.join(', ') ?? ''}
            onChange={e => {
              const raw = e.target.value
              const tags = raw ? raw.split(/[,，]\s*/).map(v => v.trim()).filter(Boolean) : undefined
              updateField('tags', tags?.length ? tags : undefined)
            }}
          />
          <Input
            label="展示名称 (display_name)"
            placeholder="如 水墨山水"
            value={form.display_name ?? ''}
            onChange={e => updateField('display_name', e.target.value || undefined)}
          />
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="主题 (theme)"
              placeholder="如 山水"
              value={form.description?.theme ?? ''}
              onChange={e => updateField('description', {
                ...form.description,
                theme: e.target.value || undefined,
              })}
            />
            <Input
              label="情绪 (emotion)"
              placeholder="如 宁静"
              value={form.description?.emotion ?? ''}
              onChange={e => updateField('description', {
                ...form.description,
                emotion: e.target.value || undefined,
              })}
            />
          </div>
          <Input
            label="关键词 (keywords)"
            placeholder="逗号分隔，如: 山水,水墨,禅意"
            value={form.description?.keywords?.join(', ') ?? ''}
            onChange={e => {
              const raw = e.target.value
              const keywords = raw ? raw.split(/[,，]\s*/).filter(Boolean) : undefined
              updateField('description', {
                ...form.description,
                keywords: keywords?.length ? keywords : undefined,
              })
            }}
          />
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between pt-3 border-t border-gray-200">
          <Button
            variant="outline"
            size="sm"
            onClick={handleReset}
            loading={updateMutation.isPending}
          >
            重置为空(继承包默认)
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={onClose}>取消</Button>
            <Button size="sm" onClick={handleSave} loading={updateMutation.isPending}>
              <Save className="w-4 h-4 mr-1" />
              保存
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  )
}

// ============================================================================
// Structured Pack Metadata Editor
// ============================================================================

function PackMetadataEditor({
  metadata,
  onChange,
  packId,
  items,
}: {
  metadata: PackMetadata
  onChange: (m: PackMetadata) => void
  packId: string
  items?: ContentPackItem[]
}) {
  const [showRawJson, setShowRawJson] = useState(false)
  const [rawText, setRawText] = useState('')

  const overlay = metadata.overlay ?? { enabled: false }
  const defaults = metadata.default_metadata ?? {}

  const updateOverlay = (partial: Partial<OverlayConfig>) => {
    onChange({ ...metadata, overlay: { ...overlay, ...partial } })
  }

  return (
    <div className="space-y-4">
      {/* Overlay section */}
      <div className="space-y-3">
        <h4 className="text-sm font-medium text-gray-700">覆盖层 (overlay)</h4>
        <div className="grid grid-cols-3 gap-3">
          <div className="flex items-end">
            <label className="flex items-center gap-2 pb-2 cursor-pointer">
              <input
                type="checkbox"
                checked={overlay.enabled}
                onChange={e => updateOverlay({ enabled: e.target.checked })}
                className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className="text-sm text-gray-700">启用</span>
            </label>
          </div>
          <Input
            label="文件名"
            placeholder="overlay.png"
            value={overlay.filename ?? ''}
            onChange={e => updateOverlay({ filename: e.target.value || undefined })}
          />
          <Select
            label="位置"
            value={overlay.position ?? 'top'}
            onChange={e => updateOverlay({ position: e.target.value })}
            options={[
              { value: 'top', label: '顶部' },
              { value: 'bottom', label: '底部' },
              { value: 'center', label: '居中' },
            ]}
          />
        </div>
      </div>

      {/* Pack-level default font */}
      <div className="pt-3 border-t border-gray-200">
        <h4 className="text-sm font-medium text-gray-700 mb-2">默认字体 (default_font_id)</h4>
        <p className="text-xs text-gray-500 mb-2">
          当 workflow 节点没有指定 font_id 时使用。未设置则回退到全局默认字体。
        </p>
        <FontPicker
          value={metadata.default_font_id ?? null}
          onChange={(fontId) => onChange({ ...metadata, default_font_id: fontId })}
          fallbackLabel="使用全局默认"
          showManageLink={false}
        />
      </div>

      {/* Default metadata — interactive editor */}
      <div className="pt-3 border-t border-gray-200">
        <h4 className="text-sm font-medium text-gray-700 mb-3">默认渲染参数 (default_metadata)</h4>
        <TextAreaEditor
          packId={packId}
          metadata={defaults}
          onChange={updated => onChange({ ...metadata, default_metadata: updated })}
          items={items}
        />
      </div>

      {/* Raw JSON toggle */}
      <div className="pt-3 border-t border-gray-200">
        <button
          type="button"
          className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
          onClick={() => {
            if (!showRawJson) setRawText(JSON.stringify(metadata, null, 2))
            setShowRawJson(!showRawJson)
          }}
        >
          <Settings2 className="w-3 h-3" />
          {showRawJson ? '收起 Raw JSON' : '查看 Raw JSON'}
        </button>
        {showRawJson && (
          <Textarea
            className="font-mono text-xs mt-2"
            rows={6}
            value={rawText}
            onChange={e => {
              setRawText(e.target.value)
              try {
                const parsed = JSON.parse(e.target.value) as PackMetadata
                onChange(parsed)
              } catch {
                // ignore invalid JSON while typing
              }
            }}
          />
        )}
      </div>
    </div>
  )
}

// ============================================================================
// Pack Detail (expanded inline)
// ============================================================================

function PackDetail({ packId, onClose }: { packId: string; onClose: () => void }) {
  const { data: pack, isLoading } = useContentPack(packId)
  const updateMutation = useUpdateContentPack()
  const deleteMutation = useDeleteContentPack()
  const uploadMutation = useUploadItems()
  const deleteItemMutation = useDeleteItem()

  const [editing, setEditing] = useState(false)
  const [editForm, setEditForm] = useState<ContentPackUpdate>({})
  const [packMetadata, setPackMetadata] = useState<PackMetadata>({})
  const [confirmDelete, setConfirmDelete] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [dragOver, setDragOver] = useState(false)
  const [selectedItem, setSelectedItem] = useState<ContentPackItem | null>(null)

  // Initialize edit form when pack loads
  const startEditing = useCallback(() => {
    if (!pack) return
    setEditForm({
      display_name: pack.display_name,
      description: pack.description,
      status: pack.status,
      sort_order: pack.sort_order,
      is_default: pack.is_default,
    })
    setPackMetadata(pack.metadata || {})
    setEditing(true)
  }, [pack])

  const handleSave = async () => {
    if (!pack) return
    await updateMutation.mutateAsync({
      id: pack.id,
      data: { ...editForm, metadata: packMetadata },
    })
    setEditing(false)
  }

  const handleDelete = async () => {
    if (!pack) return
    await deleteMutation.mutateAsync(pack.id)
    onClose()
  }

  const handleFileUpload = async (files: FileList | File[]) => {
    if (!pack) return
    const fileArray = Array.from(files)
    if (fileArray.length === 0) return
    await uploadMutation.mutateAsync({
      packId: pack.id,
      files: fileArray,
      itemType: pack.type === 'energy_outfit_image' ? 'outfit_image' : 'background',
    })
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    if (e.dataTransfer.files.length > 0) {
      handleFileUpload(e.dataTransfer.files)
    }
  }

  const handleDeleteItem = async (itemId: string) => {
    if (!pack) return
    await deleteItemMutation.mutateAsync({ packId: pack.id, itemId })
  }

  if (isLoading) {
    return (
      <div className="p-6 text-center text-gray-500">
        加载中...
      </div>
    )
  }

  if (!pack) {
    return (
      <div className="p-6 text-center text-gray-500">
        内容包未找到
      </div>
    )
  }

  return (
    <div className="border-t border-gray-200 bg-gray-50 p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h3 className="text-lg font-semibold text-gray-900">{pack.display_name}</h3>
          <StatusBadge status={pack.status} />
          {pack.is_default && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
              默认
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {!editing ? (
            <Button variant="outline" size="sm" onClick={startEditing}>
              <Edit className="w-4 h-4 mr-1" />
              编辑
            </Button>
          ) : (
            <>
              <Button variant="outline" size="sm" onClick={() => setEditing(false)}>取消</Button>
              <Button size="sm" onClick={handleSave} loading={updateMutation.isPending}>
                <Save className="w-4 h-4 mr-1" />
                保存
              </Button>
            </>
          )}
          <Button variant="outline" size="sm" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Edit Form */}
      {editing && (
        <Card className="space-y-4">
          <Input
            label="显示名称"
            value={editForm.display_name || ''}
            onChange={(e) => setEditForm(prev => ({ ...prev, display_name: e.target.value }))}
          />
          <Textarea
            label="描述"
            value={editForm.description || ''}
            onChange={(e) => setEditForm(prev => ({ ...prev, description: e.target.value }))}
            rows={3}
          />
          <div className="grid grid-cols-3 gap-4">
            <Select
              label="状态"
              value={editForm.status || 'draft'}
              onChange={(e) => setEditForm(prev => ({ ...prev, status: e.target.value }))}
              options={[
                { value: 'draft', label: '草稿' },
                { value: 'active', label: '激活' },
                { value: 'archived', label: '归档' },
              ]}
            />
            <Input
              label="排序"
              type="number"
              value={editForm.sort_order ?? 0}
              onChange={(e) => setEditForm(prev => ({ ...prev, sort_order: parseInt(e.target.value) || 0 }))}
            />
            <div className="flex items-end">
              <label className="flex items-center gap-2 pb-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={editForm.is_default || false}
                  onChange={(e) => setEditForm(prev => ({ ...prev, is_default: e.target.checked }))}
                  className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">设为默认</span>
              </label>
            </div>
          </div>
          <PackMetadataEditor
            metadata={packMetadata}
            onChange={setPackMetadata}
            packId={pack.id}
            items={pack.items}
          />
        </Card>
      )}

      {/* Info (non-editing) */}
      {!editing && pack.description && (
        <p className="text-sm text-gray-600">{pack.description}</p>
      )}

      {/* Image Grid */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-medium text-gray-700 flex items-center gap-1">
            <Image className="w-4 h-4" />
            文件列表 ({pack.items?.length || 0})
          </h4>
          <Button
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            loading={uploadMutation.isPending}
          >
            <Upload className="w-4 h-4 mr-1" />
            上传文件
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*,video/quicktime,video/mp4,.mov,.mp4"
            className="hidden"
            onChange={(e) => {
              if (e.target.files) {
                handleFileUpload(e.target.files)
                e.target.value = ''
              }
            }}
          />
        </div>

        {/* Drop zone */}
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
          onDragLeave={() => setDragOver(false)}
          onDrop={handleDrop}
          className={clsx(
            'border-2 border-dashed rounded-lg p-4 transition-colors',
            dragOver ? 'border-blue-400 bg-blue-50' : 'border-gray-300',
            (!pack.items || pack.items.length === 0) && 'py-12'
          )}
        >
          {(!pack.items || pack.items.length === 0) ? (
            <div className="text-center text-gray-500">
              <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" />
              <p className="text-sm">拖拽文件到此处或点击上传按钮</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3">
              {pack.items.map((item: ContentPackItem) => (
                <div
                  key={item.id}
                  className="group relative bg-white rounded-lg border border-gray-200 overflow-hidden cursor-pointer hover:ring-2 hover:ring-blue-300 transition-all"
                  onClick={() => setSelectedItem(item)}
                >
                  {/* Metadata indicator */}
                  {hasCustomMetadata(item.metadata) && (
                    <span className="absolute top-1 left-1 z-10 w-2 h-2 rounded-full bg-green-500" title="自定义配置" />
                  )}
                  {/* Live Photo badge */}
                  {item.metadata?.live_photo?.video_object_name && (
                    <span className="absolute bottom-1 left-1 z-10 bg-blue-500 text-white rounded px-1 text-[10px] font-medium">
                      Live
                    </span>
                  )}
                  {item.video_url && (
                    <span
                      className="absolute bottom-1 right-1 z-10 inline-flex items-center gap-1 bg-black/60 text-white rounded px-1 text-[10px] font-medium"
                      title="已绑定动态视频"
                    >
                      <Film className="w-3 h-3" />
                      {formatPlaybackSpeed(getPlaybackSpeed(item.metadata))}
                    </span>
                  )}
                  {/* Thumbnail */}
                  <div className="aspect-square bg-gray-100 flex items-center justify-center overflow-hidden">
                    {item.thumbnail_url ? (
                      <img
                        src={item.thumbnail_url}
                        alt={item.filename}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    ) : (
                      <Image className="w-8 h-8 text-gray-300" />
                    )}
                  </div>
                  {/* File info */}
                  <div className="p-2">
                    <p className="text-xs text-gray-700 truncate" title={item.filename}>
                      {item.filename}
                    </p>
                    <p className="text-xs text-gray-400">
                      {formatFileSize(item.file_size)}
                    </p>
                  </div>
                  {/* Delete overlay */}
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDeleteItem(item.id) }}
                    disabled={deleteItemMutation.isPending}
                    className="absolute top-1 right-1 p-1 rounded-full bg-red-500 text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-600"
                    title="删除"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Delete Pack */}
      <div className="border-t border-gray-200 pt-4">
        {!confirmDelete ? (
          <Button variant="danger" size="sm" onClick={() => setConfirmDelete(true)}>
            <Trash2 className="w-4 h-4 mr-1" />
            删除内容包
          </Button>
        ) : (
          <div className="flex items-center gap-3">
            <span className="text-sm text-red-600">确定要删除此内容包及所有文件吗？此操作不可撤销。</span>
            <Button variant="danger" size="sm" onClick={handleDelete} loading={deleteMutation.isPending}>
              确认删除
            </Button>
            <Button variant="outline" size="sm" onClick={() => setConfirmDelete(false)}>
              取消
            </Button>
          </div>
        )}
      </div>

      {/* Item Metadata Dialog */}
      {selectedItem && (
        <ItemMetadataDialog
          open={!!selectedItem}
          onClose={() => setSelectedItem(null)}
          item={selectedItem}
          packId={pack.id}
          items={pack.items}
        />
      )}
    </div>
  )
}

// ============================================================================
// Helpers
// ============================================================================

function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(1024))
  return `${(bytes / Math.pow(1024, i)).toFixed(i > 0 ? 1 : 0)} ${units[i]}`
}

function formatDate(dateStr?: string): string {
  if (!dateStr) return '-'
  try {
    return new Date(dateStr).toLocaleString('zh-CN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return dateStr
  }
}

// ============================================================================
// Main Page
// ============================================================================

export function ContentPacksPage() {
  const [typeFilter, setTypeFilter] = useState<string>('all')
  const [statusFilter, setStatusFilter] = useState<string>('')
  const [createOpen, setCreateOpen] = useState(false)
  const [expandedPackId, setExpandedPackId] = useState<string | null>(null)
  const migrateMutation = useMigrateFilesystem()

  const queryType = typeFilter === 'all' ? undefined : typeFilter
  const queryStatus = statusFilter || undefined
  const { data, isLoading, error } = useContentPackList(queryType, queryStatus)

  const packs = data?.packs || []

  const toggleExpand = (packId: string) => {
    setExpandedPackId(prev => prev === packId ? null : packId)
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Package className="w-6 h-6 text-gray-700" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">内容包管理</h1>
            <p className="text-sm text-gray-500 mt-0.5">管理签文主题、背景图等内容资源</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => migrateMutation.mutate()}
            loading={migrateMutation.isPending}
            title="从文件系统迁移到 OSS（仅需执行一次）"
          >
            <FolderInput className="w-4 h-4 mr-1" />
            迁移文件
          </Button>
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            <Plus className="w-4 h-4 mr-1" />
            创建内容包
          </Button>
        </div>
      </div>

      {/* Font Library — global, pack-independent */}
      <FontLibrarySection />

      {/* Filters */}
      <div className="flex items-center gap-4">
        <Tabs value={typeFilter} onValueChange={setTypeFilter}>
          <TabsList>
            <TabsTrigger value="all">全部</TabsTrigger>
            <TabsTrigger value="lucky_charm_theme">签文主题</TabsTrigger>
            <TabsTrigger value="energy_outfit_image">能量穿衣图</TabsTrigger>
            <TabsTrigger value="mbti_official_character">MBTI 人格图</TabsTrigger>
            <TabsTrigger value="mbti_contrast_character">MBTI 反差人格图</TabsTrigger>
          </TabsList>
        </Tabs>
        <Select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          options={[
            { value: '', label: '所有状态' },
            { value: 'draft', label: '草稿' },
            { value: 'active', label: '已激活' },
            { value: 'archived', label: '已归档' },
          ]}
          className="w-32"
        />
      </div>

      {/* Content */}
      {isLoading && (
        <div className="text-center py-12 text-gray-500">加载中...</div>
      )}

      {error && (
        <div className="text-center py-12 text-red-500">
          加载失败: {(error as Error).message}
        </div>
      )}

      {!isLoading && !error && packs.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          <Package className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p>暂无内容包</p>
          <p className="text-sm mt-1">点击"创建内容包"按钮开始</p>
        </div>
      )}

      {!isLoading && !error && packs.length > 0 && (
        <div className="space-y-3">
          {packs.map((pack: ContentPack) => (
            <div key={pack.id} className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
              {/* Pack Card */}
              <button
                onClick={() => toggleExpand(pack.id)}
                className="w-full text-left px-6 py-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3 min-w-0">
                    <Package className="w-5 h-5 text-gray-400 flex-shrink-0" />
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-gray-900">{pack.display_name}</span>
                        <TypeBadge type={pack.type} />
                        <StatusBadge status={pack.status} />
                        {pack.is_default && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                            默认
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {pack.name} | {pack.item_count} 个文件 | 创建于 {formatDate(pack.created_at)}
                      </p>
                    </div>
                  </div>
                  <div className="flex-shrink-0 ml-4">
                    {expandedPackId === pack.id ? (
                      <ChevronUp className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </div>
              </button>

              {/* Expanded Detail */}
              {expandedPackId === pack.id && (
                <PackDetail
                  packId={pack.id}
                  onClose={() => setExpandedPackId(null)}
                />
              )}
            </div>
          ))}
        </div>
      )}

      {/* Total count */}
      {data && data.total > 0 && (
        <div className="text-sm text-gray-500 text-right">
          共 {data.total} 个内容包
        </div>
      )}

      {/* Create Dialog */}
      <CreatePackDialog open={createOpen} onClose={() => setCreateOpen(false)} />
    </div>
  )
}
